import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { AxiosError } from 'axios'
import { Button, Input, FormField, ErrorMessage } from '@/components/common'
import { useAuth } from '@/hooks/useAuth'
import { loginSchema, type LoginFormData } from '@/utils/validationSchemas'
import { ERROR_MESSAGES, LOADING_MESSAGES } from '@/utils/constants'
import type { ErrorResponse } from '@/types/api.types'

export default function LoginForm() {
  const { login } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: 'onBlur',
  })

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true)
    setApiError(null)

    try {
      await login(data.policyNumber, data.pin)
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorResponse = error.response?.data as ErrorResponse | undefined

        if (error.response?.status === 401) {
          setApiError(errorResponse?.message || ERROR_MESSAGES.INVALID_CREDENTIALS)
        } else if (error.response?.status === 429) {
          setApiError('Too many login attempts. Please try again later.')
        } else if (error.response?.status === 500) {
          setApiError('Server error. Please try again later.')
        } else if (!error.response) {
          setApiError(ERROR_MESSAGES.NETWORK)
        } else {
          setApiError(errorResponse?.message || ERROR_MESSAGES.GENERIC)
        }
      } else {
        setApiError(ERROR_MESSAGES.GENERIC)
      }

      console.error('Login error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* API Error Message */}
      {apiError && (
        <ErrorMessage message={apiError} variant="banner" />
      )}

      {/* Policy Number Input */}
      <FormField
        label="Policy Number"
        htmlFor="policyNumber"
        required
        error={errors.policyNumber?.message}
        hint="Example: ABC1234567"
      >
        <Input
          id="policyNumber"
          {...register('policyNumber')}
          placeholder="Enter your policy number"
          error={!!errors.policyNumber}
          disabled={isLoading}
          autoComplete="off"
          maxLength={13}
        />
      </FormField>

      {/* Reference Number (PIN) Input */}
      <FormField
        label="Reference Number"
        htmlFor="pin"
        required
        error={errors.pin?.message}
        hint="Your reference number was provided on your statement."
      >
        <Input
          id="pin"
          {...register('pin')}
          type="text"
          placeholder="Enter your reference number"
          error={!!errors.pin}
          disabled={isLoading}
          autoComplete="off"
          maxLength={10}
        />
      </FormField>

      {/* Submit Button */}
      <div className="pt-2">
        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          loading={isLoading}
          disabled={isLoading}
        >
          {isLoading ? LOADING_MESSAGES.LOGGING_IN : 'View Bill'}
        </Button>
      </div>
    </form>
  )
}
